import { Injectable } from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {filter, first, map, tap} from 'rxjs/operators';
import {
  EMPTY_WHEN_FILTER,
  MenuAddendum,
  MenuCategory,
  MenuItem,
  MenuItemPushGroup,
  MenuSection,
  MenuStructure,
  WhenFilter
} from '../models/FSMenu';
import {FireService} from './fire.service';
import moment from 'moment';
import {ConfigService} from './config.service';
import {findPlausibleNonLoggedInDeliverType, hasMoreThanOneDeliverType, VenueConfig} from '../models/FSConfig';
import {OrderService} from './order.service';
import {Order} from '../models/Order';
import {Moment} from 'moment';
import {MenuUtils} from '../utils/Utils';
import {TranslateService} from './translate.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private currentMenuSubject = new BehaviorSubject<MenuStructure | null>(null);
  private currentAddendumSubject = new BehaviorSubject<MenuAddendum>({items: []} as MenuAddendum);
  private venueId?: string;
  private menuSub?: Subscription;
  private addendumSub?: Subscription;

  constructor(private fire: FireService, private config: ConfigService, private orderService: OrderService) { }

  observeUnfilteredMenu(venueId: string): Observable<MenuStructure> {
    if (venueId && venueId !== this.venueId) {
      this.initMenuService(venueId);
    }
    return this.currentMenuSubject.pipe(filter(m=> m !== null));
  }

  getAddendum(): MenuAddendum {
    return this.currentAddendumSubject.getValue();
  }

  getMenuStructure(): MenuStructure | null {
    return this.currentMenuSubject?.getValue();
  }

  observeAddendum(): Observable<MenuAddendum> {
    return this.currentAddendumSubject;
  }

  private initMenuService(venueId: string) {
    this.menuSub?.unsubscribe();
    this.addendumSub?.unsubscribe();
    this.venueId = venueId;
    console.log(`Init menu service ${venueId}`);

    this.menuSub = this.fire.observeMenuStructure(venueId).subscribe( m => {
      console.log(`Menu state: ${m?.food?.length} food items `);
      this.currentMenuSubject.next(m);
    });

    this.addendumSub = this.fire.observeMenuAddendum(venueId).subscribe( ad => {
      console.log("Addendum state:", ad);
      this.currentAddendumSubject.next(ad);
    });
  }

  observeMenuConfigOrder(venueId: string): Observable<[MenuStructure, VenueConfig, Order]> {
    return combineLatest([
      this.observeUnfilteredMenu(venueId),
      //this.config.observeConfig(venueId),
      this.config.currentConfigSubject,
      this.orderService.currentOrder()]
    ).pipe(
      map(([menu, cfg, order]) => {
        this.updateHiddenFlag(menu, cfg, order);
        return [menu, cfg, order];
      })
    );
  }

  updateHiddenFlag(menu: MenuStructure, cfg: VenueConfig, order: Order) {
    const now = moment();
    const itemFilter = MenuUtils.buildFilter(order, cfg);
    if (menu.food) {
      MenuUtils.updatePageHiddenFlag(menu.food, cfg, order, now, "mhide", false, false, order?.user_cfg?.when_tag, itemFilter!);
    }
    if (menu.drinks) {
      MenuUtils.updatePageHiddenFlag(menu.drinks, cfg, order, now, "mhide", false, false, order?.user_cfg?.when_tag, itemFilter!);
    }
  }

  getFilteredMenuItemsFromGroup(group: MenuItemPushGroup, menu?: MenuStructure): MenuItem[] {
    const currentMenu = menu ?? this.getMenuStructure();
    if (!currentMenu) {
      return [];
    }
    return group.items
    .map((it) => MenuUtils.getItemInMenu(it, currentMenu))
    .filter(it => it !== null && !this.isOutOfStock(it.id) && !this.isAlcoholButNotAllowed(it) && MenuUtils.isWhenVisible(it, moment()));
  }

  getItem(itemId: string, venueId: string): Observable<MenuItem | null> {
    return this.observeUnfilteredMenu(venueId).pipe(map( menu => MenuUtils.getItemInMenu(itemId, menu)), first());
  }

  isMenuEmpty(rootCategories: MenuCategory[], sections: MenuSection[]): boolean {
    if (!MenuUtils.hasVisibleRootCategories(rootCategories) && !MenuUtils.hasVisibleSections(sections)) {
      return true;
    }
    return false;
  }

  isOutOfStock(itemId: string) {
    // @ts-ignore
    const invc = this.getAddendum()?.items?.find( it => it.id === itemId)?.inventory_count;
    return invc != null && invc <= 0;
  }

  isAlcoholButNotAllowed(it: MenuItem) {
    const isTakeaway = this.orderService.getOrderOrNull()?.user_cfg?.dt === "takeaway";
    return isTakeaway && it.alc;
  }

  buildVisibleItems(items: MenuItem[]) {
    for (const item of items) {
      item.currentOrderCount = this.orderService.getCount(item.id, false, item.package != null);
      item.currentStockState = this.isOutOfStock(item.id);
    }
  }

}
